import { useState } from 'react';
import {
  DollarSign,
  Settings,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useFinancialSettings,
  useFinancialSyncStatus,
  useFinancialSync,
} from '../../hooks/useFinancial';
import { StripeIntegrationForm } from './StripeIntegrationForm';

export function StripeIntegrationCard() {
  const [showForm, setShowForm] = useState(false);
  const { data: settingsData } = useFinancialSettings();
  const { data: statusData } = useFinancialSyncStatus();
  const syncMutation = useFinancialSync();

  const settings = settingsData?.data;
  const syncStatus = statusData?.data;
  const isConfigured = settings?.enabled && settings?.mcpServerCommand;

  function handleSync() {
    syncMutation.mutate(undefined, {
      onSuccess: () => toast.success('Sync financeiro iniciado'),
      onError: () => toast.error('Erro ao iniciar sync'),
    });
  }

  function formatDate(date: string | null | undefined) {
    if (!date) {
      return 'Nunca';
    }
    return new Date(date).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-secondary-200 p-5 hover:shadow-sm transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-2.5 h-2.5 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-secondary-300'}`}
            />
            <div>
              <h3 className="font-semibold text-secondary-900">Stripe</h3>
              <p className="text-xs text-secondary-500">
                Pagamentos e assinaturas · Sync via MCP Server
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {isConfigured && (
              <button
                onClick={handleSync}
                disabled={syncMutation.isPending || syncStatus?.isRunning}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 disabled:opacity-50"
              >
                {syncMutation.isPending || syncStatus?.isRunning ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                Sync
              </button>
            )}
            <button
              onClick={() => setShowForm(true)}
              className="p-2 rounded-lg hover:bg-secondary-100 text-secondary-500"
            >
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>

        {isConfigured ? (
          <>
            <div className="text-sm text-secondary-600 space-y-1">
              <p>Intervalo: a cada {settings.syncIntervalMinutes} min</p>
              {syncStatus && (
                <p className="text-xs text-secondary-400">
                  {syncStatus.customersCount} clientes · {syncStatus.subscriptionsCount} assinaturas
                  · {syncStatus.paymentsCount} pagamentos
                </p>
              )}
            </div>

            <div className="mt-3 pt-3 border-t border-secondary-100 flex items-center gap-2 text-sm">
              {syncStatus?.lastSyncError ? (
                <XCircle className="w-4 h-4 text-red-600" />
              ) : syncStatus?.isRunning ? (
                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
              ) : syncStatus?.lastSyncAt ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <Clock className="w-4 h-4 text-secondary-400" />
              )}
              <span>Ultima sync: {formatDate(syncStatus?.lastSyncAt)}</span>
            </div>

            {syncStatus?.lastSyncError && (
              <p className="mt-2 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-1.5">
                {syncStatus.lastSyncError}
              </p>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 mt-2 text-sm text-secondary-400">
            <DollarSign className="w-4 h-4" />
            <span>Nao configurado — clique em configurar para conectar sua conta Stripe</span>
          </div>
        )}
      </div>

      {showForm && <StripeIntegrationForm onClose={() => setShowForm(false)} />}
    </>
  );
}
